import { CartAddEvent } from '@theme/events';

/**
 * Kampagnen-Zeitfenster im Browser prüfen. Der Server rendert den Status beim Aufbau der Seite;
 * gecachtes HTML kann die Grenze (00:00) aber überdauern. Liefert true, wenn jetzt im Fenster.
 * @param {HTMLElement} el - Element mit data-kampagne-von/-bis (optional data-kampagne-vorschau)
 */
export function kampagneLive(el) {
  if (el.hasAttribute('data-kampagne-vorschau')) return true;
  const now = Date.now();
  const von = Date.parse(el.dataset.kampagneVon || '');
  const bis = Date.parse(el.dataset.kampagneBis || '');
  if (!Number.isNaN(von) && now < von) return false;
  if (!Number.isNaN(bis) && now > bis) return false;
  return true;
}

/**
 * <lhc-kampagne>: blendet seinen Inhalt nur im Kampagnenfenster ein
 * (mit data-kampagne-invertiert umgekehrt: nur außerhalb).
 */
class LhcKampagne extends HTMLElement {
  connectedCallback() {
    const live = kampagneLive(this);
    this.hidden = this.hasAttribute('data-kampagne-invertiert') ? live : !live;
  }
}

if (!customElements.get('lhc-kampagne')) customElements.define('lhc-kampagne', LhcKampagne);

/**
 * <lhc-aktion-rail>: horizontale Kartenreihe mit Vor/Zurück-Buttons (Scroll-Snap).
 */
class LhcAktionRail extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resize = null;

  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.#step(-1));
    this.next?.addEventListener('click', () => this.#step(1));
    this.track.addEventListener('scroll', this.#update, { passive: true });
    this.track.addEventListener('pointerdown', this.#dragStart);
    this.track.addEventListener('click', this.#blockClickAfterDrag, true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
    this.#resize = new ResizeObserver(this.#update);
    this.#resize.observe(this.track);
    requestAnimationFrame(this.#update);
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
  }

  #step(dir) {
    const slide = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = slide instanceof HTMLElement ? slide.offsetWidth + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: dir * width, behavior: 'smooth' });
  }

  #drag = { x: 0, left: 0, moved: false };

  #dragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.hasAttribute('data-scrollable')) return;
    this.#drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
    this.track.addEventListener('pointermove', this.#dragMove);
    this.track.addEventListener('pointerup', this.#dragEnd);
    this.track.addEventListener('pointercancel', this.#dragEnd);
  };

  #dragMove = (event) => {
    const dx = event.clientX - this.#drag.x;
    if (!this.#drag.moved) {
      if (Math.abs(dx) < 5) return;
      this.#drag.moved = true;
      this.track.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
    }
    this.track.scrollLeft = this.#drag.left - dx;
  };

  #dragEnd = (event) => {
    this.track.removeEventListener('pointermove', this.#dragMove);
    this.track.removeEventListener('pointerup', this.#dragEnd);
    this.track.removeEventListener('pointercancel', this.#dragEnd);
    if (!this.#drag.moved) return;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.#snapToNearest();
  };

  #snapToNearest() {
    const start = this.track.getBoundingClientRect().left + (parseFloat(getComputedStyle(this.track).paddingLeft) || 0);
    let target = this.track.scrollLeft;
    let best = Infinity;
    for (const slide of this.track.children) {
      const offset = slide.getBoundingClientRect().left - start;
      if (Math.abs(offset) < best) {
        best = Math.abs(offset);
        target = this.track.scrollLeft + offset;
      }
    }
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.track.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    window.setTimeout(() => this.removeAttribute('data-dragging'), 400);
  }

  #blockClickAfterDrag = (event) => {
    if (!this.#drag.moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.#drag.moved = false;
  };

  #update = () => {
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    const scrollable = scrollWidth > clientWidth + 1;
    this.toggleAttribute('data-scrollable', scrollable);
    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  };
}

if (!customElements.get('lhc-aktion-rail')) customElements.define('lhc-aktion-rail', LhcAktionRail);

/**
 * Quick-Add für Aktions- und Bundle-Karten: [data-lhc-add="<variant-id>"].
 * Aktualisiert die Warenkorb-Sections wie product-form.js und löst CartAddEvent aus
 * (öffnet den Drawer, wenn er auto-open hat).
 */
async function quickAdd(button) {
  const id = Number(button.dataset.lhcAdd);
  if (!id || button.disabled) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'cart/add failed');

    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    document.dispatchEvent(
      new CartAddEvent(cart, `lhc-aktion-${id}`, {
        source: 'lhc-aktion',
        itemCount: 1,
        variantId: String(id),
        sections: data.sections,
      })
    );
    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 1600);
  } catch (error) {
    console.error(error);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

if (!window.__lhcAktionQuickAdd) {
  window.__lhcAktionQuickAdd = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-lhc-add]') : null;
    if (!target) return;
    event.preventDefault();
    quickAdd(target);
  });
}
